// This static import is classic-script-safe only while no module in the
// dynamically-imported full bundle (full.ts / Widget.svelte / components)
// imports the same file — if one does, rollup splits it into a shared chunk
// and this entry becomes an ES-import facade, a syntax error in the classic
// `<script async>` embed.
import {
  DEV_SUPPORT_NUMBER,
  baseMarketForStorefrontHost,
  getSmsSupportNumber,
  getSmsSupportNumberForLocale,
  getWhatsAppSupportNumber,
  normalizeSupportBrand
} from "../../../packages/shared/src/support-phone-numbers";
import { resolveWidgetAssetBase } from "./asset-base";
import { launcherLabel } from "./stub-labels";

// Lazy-load stub entry.
//
// Registers the <ridestore-chat> custom element with just the launcher button
// visible — no Svelte runtime, no i18n catalogs, no conversation UI. On first
// click, dynamic-imports `./full` which boots the real Svelte widget into
// the same shadow root.
//
// Result: zero framework cost on every page that never opens chat (~95% of
// pageviews on a typical storefront). Customers who do open chat pay the
// full bundle exactly when they need it — not during initial paint.

interface ChatConfig {
  apiUrl?: string;
  brand?: string;
  locale?: string;
  /** Storefront ISO alpha-2 country when the locale has no region (for example `SE`). */
  country?: string;
  email?: string;
  phoneNumber?: string;
  environment?: string;
  theme?: Record<string, string>;
  /** Override the launcher z-index. Defaults to 95 (below host modals). */
  zIndex?: number;
  /**
   * Controls whether Powder renders its own floating launcher. Use "none"
   * when the host page provides every chat entry point itself.
   */
  launcher?: "default" | "none";
  /**
   * Start fully invisible — no launcher button, nothing painted — until the
   * host calls `RidestoreChat.open()`. The first open reveals the widget and
   * locks it visible for the rest of the session. Rare; defaults to false.
   */
  ghost?: boolean;
}

interface ChannelAvailability {
  whatsapp: boolean;
  sms: boolean;
}

interface ChatApi {
  /**
   * Open the chat panel programmatically. If the full widget hasn't loaded
   * yet, this triggers the same lazy-load path as a launcher click; if it is
   * already loaded but minimized, the panel reopens. If no `<ridestore-chat>`
   * element is on the page yet, one is auto-created from
   * `window.RIDESTORE_CHAT` config first. Resolves once the panel is open
   * (or rejects if the bundle fails to load).
   */
  open(): Promise<void>;
  /**
   * Toggle the chat panel exactly like a tap on the launcher bubble: opens
   * it when closed (lazy-loading the widget on first use), minimizes it when
   * open. Resolves with the new open state. The recommended call for custom
   * "Chat with us" buttons on the host page.
   */
  toggle(): Promise<boolean>;
  /**
   * Tear down the full widget and re-render the launcher stub. No-op if the
   * widget hasn't been activated. State persists in localStorage, so a
   * subsequent `open()` resumes the same conversation.
   */
  close(): void;
  /**
   * Update the customer email after the embed has loaded. Pass null/undefined
   * or an empty string to clear it.
   */
  setEmail(email?: string | null): void;
  /**
   * Update the customer phone number after the embed has loaded. Pass
   * null/undefined or an empty string to clear it.
   */
  setPhoneNumber(phoneNumber?: string | null): void;
  /**
   * Which external channels apply for the current config. Host pages should
   * check this before rendering their own WhatsApp/SMS buttons so customers
   * never see a button that does nothing. Mirrors the widget's own rules:
   * WhatsApp everywhere; SMS on touch devices when the active region + brand
   * has a configured destination in the shared support-number registry.
   */
  channels(): ChannelAvailability;
  /**
   * Open a WhatsApp conversation with the brand's support number, without
   * loading the chat widget. Returns false if WhatsApp is unavailable.
   */
  whatsapp(): boolean;
  /**
   * Open the device SMS composer addressed to the brand's support number.
   * Returns false if SMS is unavailable for the current locale/device.
   */
  sms(): boolean;
}

declare global {
  interface Window {
    RIDESTORE_CHAT?: ChatConfig;
    RidestoreChat?: ChatApi;
  }
  // Base URL used by the absoluteDynamicImportPlugin in vite.config.ts to
  // resolve every dynamic `import()` target to an absolute URL. Captured
  // here at synchronous module evaluation time — `document.currentScript`
  // is null inside any later async callback, so this must run before any
  // import() fires.
  // eslint-disable-next-line no-var
  var __RC_BASE__: string | undefined;
}

const embedScriptUrl =
  (document.currentScript as HTMLScriptElement | null)?.src ??
  findEmbedScriptUrl();

// The Worker owns the production asset set, so a legacy Pages-hosted entry
// still loads its full bundle and locale catalogs from the configured Worker.
// Deploy-window skew is accepted for those legacy entries: the Worker deploys
// before Pages, so an entry loaded just before a release can request a chunk
// hash the new Worker no longer serves and fail until the page reloads. The
// canonical Worker-hosted entry swaps entry and chunks atomically and cannot
// skew. Previews follow their configured PR/scratch Worker. Invalid or absent
// API config falls back to the embedding script (or page) instead of breaking
// the storefront during synchronous script evaluation.
self.__RC_BASE__ = resolveWidgetAssetBase(
  window.RIDESTORE_CHAT?.apiUrl,
  embedScriptUrl,
  self.location.href
);

function findEmbedScriptUrl(): string | undefined {
  const scripts = Array.from(document.scripts).reverse();
  return scripts.find((script) =>
    /(?:^|\/)ridestore-chat\.js(?:[?#]|$)/.test(script.src)
  )?.src;
}

// Launcher styles. Kept hand-inlined here (rather than imported from
// widget.css) so the stub stays self-contained — pulling in the full
// stylesheet would defeat the purpose of having a stub.
//
// Colors match `--rs-primary` / `--ds-ink-600` in widget.css. If the brand
// palette changes there, update this string too.
//
// Launcher z-index: default 95 sits below the storefront's modal tier (z >= 99)
// so the bubble yields to cart drawers / consent instead of poking through.
// Host-overridable via --rs-z-index (`z-index` attr). Mirror in widget.css.
const STUB_CSS = `:host{all:initial}
.l{position:fixed;right:28px;bottom:28px;width:56px;height:56px;background:#121212;color:#fff;border:none;cursor:pointer;border-radius:999px;display:grid;place-items:center;box-shadow:0 12px 28px rgba(0,0,0,.22),0 2px 6px rgba(0,0,0,.12);z-index:var(--rs-z-index,95);transition:transform 200ms cubic-bezier(.23,1,.32,1),background 200ms cubic-bezier(.23,1,.32,1);padding:0}
@media (hover:hover) and (pointer:fine){.l:hover{background:#282828;transform:translateY(-1px)}}
.l:active{transform:scale(.96);opacity:.94}
.l svg{width:22px;height:22px;stroke:#fff;stroke-width:1.6;fill:none;stroke-linecap:round;stroke-linejoin:round}`;

// Same chat-bubble SVG path as components/Icons.svelte renders for `name="chat"`.
const CHAT_SVG =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>';

type FullModule = typeof import("./full.js");
type WidgetHandle = ReturnType<FullModule["activate"]>;
let fullPromise: Promise<FullModule> | null = null;
function loadFull(): Promise<FullModule> {
  if (!fullPromise) {
    fullPromise = import("./full.js").catch((err) => {
      fullPromise = null;
      throw err;
    });
  }
  return fullPromise;
}

class RidestoreChat extends HTMLElement {
  // Config attributes set by autoMount() or by hand. Stored as plain props
  // and handed off to the full widget at activation time. We mirror them on
  // the element rather than reading attributes inside _activate so callers
  // who set `el.apiUrl = "..."` after mount also work (matches the previous
  // embed.ts contract).
  apiUrl?: string;
  brand?: string;
  locale?: string;
  country?: string;
  environment?: string;
  zIndex?: number;
  launcher: "default" | "none" = "default";
  ghost?: boolean;
  private _email?: string;
  private _phoneNumber?: string;
  // One-way latch: ghost hides the widget until the first open(), after which
  // it behaves like a normal embed (launcher visible) for the rest of the
  // session. Never flips back to hidden.
  private _ghostLocked = false;
  private _loading = false;
  // Handle to the mounted full widget; read by the global API to drive the
  // panel after first activation (when the stub no longer owns the UI).
  _full: WidgetHandle | null = null;

  get email(): string | undefined {
    return this._email;
  }

  set email(value: string | undefined) {
    const email = typeof value === "string" ? value : undefined;
    this._email = email;
    this._full?.setEmail(email);
  }

  get phoneNumber(): string | undefined {
    return this._phoneNumber;
  }

  set phoneNumber(value: string | undefined) {
    const phoneNumber = typeof value === "string" ? value : undefined;
    this._phoneNumber = phoneNumber;
    this._full?.setPhoneNumber(phoneNumber);
  }

  static get observedAttributes() {
    return ["api-url", "brand", "locale", "country", "email", "phone-number", "environment", "env", "z-index", "launcher", "ghost"];
  }

  attributeChangedCallback(name: string, _old: string | null, value: string | null) {
    if (name === "email") {
      this.email = value ?? undefined;
      return;
    }
    if (name === "phone-number") {
      this.phoneNumber = value ?? undefined;
      return;
    }
    if (value == null) return;
    if (name === "api-url") this.apiUrl = value;
    else if (name === "brand") this.brand = value;
    else if (name === "locale") {
      this.locale = value;
      this.shadowRoot?.querySelector(".l")?.setAttribute("aria-label", launcherLabel(value));
    }
    else if (name === "country") this.country = value;
    else if (name === "environment" || name === "env") this.environment = value;
    else if (name === "z-index") {
      const n = parseInt(value, 10);
      if (!isNaN(n)) this.zIndex = n;
    }
    else if (name === "launcher") this.launcher = value === "none" ? "none" : "default";
    // Bare `<ridestore-chat ghost>` arrives as value "" → true; ghost="false" opts out.
    else if (name === "ghost") this.ghost = value !== "false";
  }

  connectedCallback() {
    const root = this.shadowRoot ?? this.attachShadow({ mode: "open" });
    if (this.ghost && !this._ghostLocked) return;
    if (!this._full && !root.querySelector("button")) this._renderStub(root);
  }

  disconnectedCallback() {
    this._deactivate();
  }

  _deactivate() {
    if (!this._full) return;
    this._full.destroy();
    this._full = null;
    this._loading = false;
    if (this.shadowRoot) this._renderStub(this.shadowRoot);
  }

  private _renderStub(root: ShadowRoot) {
    if (this.launcher === "none") {
      root.replaceChildren();
      return;
    }
    root.innerHTML = `<style>${STUB_CSS}</style><button class="l" aria-haspopup="dialog" aria-expanded="false">${CHAT_SVG}</button>`;
    // CSS custom properties cascade into the shadow DOM even through `all:initial`,
    // so setting --rs-z-index on the host element is all we need.
    if (this.zIndex != null) this.style.setProperty("--rs-z-index", String(this.zIndex));
    const btn = root.querySelector("button");
    if (btn) {
      btn.setAttribute("aria-label", launcherLabel(this.locale));
      btn.addEventListener("click", () => void this._activate());
    }
  }

  async _activate() {
    if (this._loading || this._full) return;
    this._ghostLocked = true;
    this._loading = true;
    let opener = document.activeElement;
    while (opener?.shadowRoot?.activeElement) opener = opener.shadowRoot.activeElement;
    const btn = this.shadowRoot?.querySelector<HTMLButtonElement>("button");
    btn?.setAttribute("aria-busy", "true");
    if (btn) btn.disabled = true;
    try {
      const mod = await loadFull();
      if (!this.isConnected) return;
      const channels = supportChannels(this.brand, this.locale, this.country, this.environment);
      this._full = mod.activate(this, {
        apiUrl: this.apiUrl,
        brand: this.brand,
        locale: this.locale,
        country: this.country,
        email: this.email,
        phoneNumber: this.phoneNumber,
        environment: this.environment,
        whatsappPhone: channels.whatsappPhone,
        smsPhone: channels.smsPhone ?? undefined,
        zIndex: this.zIndex,
        launcher: this.launcher,
        initialOpener: opener instanceof HTMLElement ? opener : null
      });
    } catch (err) {
      console.warn("[ridestore-chat] failed to load widget", err);
      if (btn) {
        btn.disabled = false;
        btn.removeAttribute("aria-busy");
      }
    } finally {
      this._loading = false;
    }
  }
}

if (!customElements.get("ridestore-chat")) {
  customElements.define("ridestore-chat", RidestoreChat);
}

function createChatElement(cfg: ChatConfig): RidestoreChat {
  const el = document.createElement("ridestore-chat") as RidestoreChat;
  if (cfg.apiUrl) {
    el.setAttribute("api-url", cfg.apiUrl);
    el.apiUrl = cfg.apiUrl;
  }
  if (cfg.brand) {
    el.setAttribute("brand", cfg.brand);
    el.brand = cfg.brand;
  }
  if (cfg.locale) {
    el.setAttribute("locale", cfg.locale);
    el.locale = cfg.locale;
  }
  if (cfg.country) {
    el.setAttribute("country", cfg.country);
    el.country = cfg.country;
  }
  if (cfg.email) {
    el.setAttribute("email", cfg.email);
    el.email = cfg.email;
  }
  if (cfg.phoneNumber) {
    el.setAttribute("phone-number", cfg.phoneNumber);
    el.phoneNumber = cfg.phoneNumber;
  }
  if (cfg.environment) {
    el.setAttribute("environment", cfg.environment);
    el.environment = cfg.environment;
  }
  if (cfg.zIndex != null) el.zIndex = cfg.zIndex;
  if (cfg.launcher === "none") {
    el.setAttribute("launcher", "none");
    el.launcher = "none";
  }
  if (cfg.ghost) {
    el.setAttribute("ghost", "");
    el.ghost = true;
  }
  return el;
}

function autoMount() {
  if (document.querySelector("ridestore-chat")) return;
  const el = createChatElement(window.RIDESTORE_CHAT ?? {});
  document.body.appendChild(el);
}

function isDevEnvironment(environment?: string): boolean {
  const env = environment?.toLowerCase().trim();
  return env === "dev" || env === "development";
}

// Market path segments are a two-letter code, optionally with a language
// suffix: /se, /eu, /ca, /ca-fr, /ch-it. Content paths on the base market
// (/product/x, /customer-service) never take that shape. The suffix names the
// language, never a different number, so the base segment is the market.
const MARKET_SEGMENT_PATTERN = /^([a-z]{2})(?:-[a-z]{2})?$/;

/**
 * The market of the page we're embedded on: the path's own market segment when
 * it has one, else the host's base market. Mirrors the worker's
 * `smsRegionForStorefrontUrl` so a storefront resolves the same way on both
 * sides.
 *
 * Reading the segment matters even though every pathed market currently ships a
 * regional locale — the documented embed config is a bare `locale: "en"` plus an
 * optional `country`, so a market that follows the docs would otherwise silently
 * show no number. Null for an unknown host (dev demo, localhost): fails closed.
 */
function marketForCurrentPage(): string | null {
  if (typeof window === "undefined") return null;
  // Host-gate BOTH tiers. A market segment only means a market on a storefront
  // we recognise — otherwise any page could mint a support number by sitting at
  // a /us/ path (a dev server, a preview host, someone else's site).
  const baseMarket = baseMarketForStorefrontHost(window.location.hostname);
  if (!baseMarket) return null;
  const firstSegment = window.location.pathname.split("/").find((s) => s.length > 0);
  const marketSegment = firstSegment ? MARKET_SEGMENT_PATTERN.exec(firstSegment.toLowerCase()) : null;
  return marketSegment?.[1] ?? baseMarket;
}

function supportChannels(
  brand?: string,
  locale?: string,
  country?: string,
  environment?: string
): { whatsappPhone: string; smsPhone: string | null } {
  const resolvedBrand = normalizeSupportBrand(brand);
  // A regional locale ("en-CA", "sv-SE") names the market outright. Failing
  // that, the host-sent country, then the page's own market — its path segment
  // if it has one, else the host's base market. That last tier is what makes a
  // storefront work when it ships the documented bare `locale: "en"`.
  const configuredSmsPhone =
    getSmsSupportNumberForLocale(locale, resolvedBrand)
    ?? getSmsSupportNumber(country, resolvedBrand)
    ?? getSmsSupportNumber(marketForCurrentPage(), resolvedBrand);
  const isDev = isDevEnvironment(environment);
  return {
    whatsappPhone: isDev ? DEV_SUPPORT_NUMBER : getWhatsAppSupportNumber(resolvedBrand),
    smsPhone: configuredSmsPhone ? (isDev ? DEV_SUPPORT_NUMBER : configuredSmsPhone) : null
  };
}

// window.RIDESTORE_CHAT is host-authored — a non-string value must not throw
// a TypeError into the storefront's own click handler.
function asString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

// Config for the channel deep links. Prefers the mounted element's props
// (covers attribute-configured embeds) and falls back to the window config,
// matching the precedence autoMount() establishes.
function channelContext(): { whatsappPhone: string; smsPhone: string | null } {
  const el = document.querySelector<RidestoreChat>("ridestore-chat");
  const cfg = window.RIDESTORE_CHAT ?? {};
  return supportChannels(
    asString(el?.brand ?? cfg.brand),
    asString(el?.locale ?? cfg.locale),
    asString(el?.country ?? cfg.country),
    asString(el?.environment ?? cfg.environment)
  );
}

// Programmatic API for host pages. `open`/`close` let a "Talk to support"
// link (or any custom CTA on the embedding site) trigger the same lazy-load
// path as a launcher click — the element method `_activate()` already handles
// "already loading" and "already activated" guards, so repeated calls are
// safe. `whatsapp`/`sms` jump straight to the external channel without
// loading the widget bundle at all; `channels()` tells the host which of
// those buttons to render.
function ensureChatElement(): RidestoreChat {
  let el = document.querySelector<RidestoreChat>("ridestore-chat");
  if (!el) {
    el = createChatElement(window.RIDESTORE_CHAT ?? {});
    document.body.appendChild(el);
  }
  return el;
}

function updateConfiguredEmail(email: string | null | undefined) {
  const next = typeof email === "string" && email.trim() ? email.trim() : undefined;
  window.RIDESTORE_CHAT = window.RIDESTORE_CHAT ?? {};
  if (next) {
    window.RIDESTORE_CHAT.email = next;
  } else {
    delete window.RIDESTORE_CHAT.email;
  }

  const el = ensureChatElement();
  el.email = next;
  if (next) {
    el.setAttribute("email", next);
  } else {
    el.removeAttribute("email");
  }
}

function updateConfiguredPhoneNumber(phoneNumber: string | null | undefined) {
  const next = typeof phoneNumber === "string" && phoneNumber.trim() ? phoneNumber.trim() : undefined;
  window.RIDESTORE_CHAT = window.RIDESTORE_CHAT ?? {};
  if (next) {
    window.RIDESTORE_CHAT.phoneNumber = next;
  } else {
    delete window.RIDESTORE_CHAT.phoneNumber;
  }

  const el = ensureChatElement();
  el.phoneNumber = next;
  if (next) {
    el.setAttribute("phone-number", next);
  } else {
    el.removeAttribute("phone-number");
  }
}

const chatApi: ChatApi = {
  async open() {
    const el = ensureChatElement();
    // Already mounted (customer opened chat before, maybe minimized it):
    // the stub no longer owns the UI, so drive the panel via the handle.
    if (el._full) {
      el._full.openPanel();
      return;
    }
    await el._activate();
  },
  async toggle() {
    const el = ensureChatElement();
    if (el._full) return el._full.toggle();
    // Never opened yet: a toggle from the stub state means "open".
    await el._activate();
    return true;
  },
  close() {
    const el = document.querySelector<RidestoreChat>("ridestore-chat");
    el?._deactivate();
  },
  setEmail(email) {
    updateConfiguredEmail(email);
  },
  setPhoneNumber(phoneNumber) {
    updateConfiguredPhoneNumber(phoneNumber);
  },
  channels() {
    // SMS requires a configured destination for the active region + brand,
    // plus a touch device — `sms:` links are dead ends on most desktops.
    return {
      whatsapp: true,
      sms:
        channelContext().smsPhone !== null &&
        matchMedia("(pointer: coarse)").matches
    };
  },
  whatsapp() {
    if (!chatApi.channels().whatsapp) return false;
    // Synthesized anchor click instead of window.open: anchors inherit the
    // user gesture more leniently (a host handler that awaits analytics
    // before calling us would get popup-blocked on Safari/Firefox), and
    // window.open with noopener returns null even on success, so a blocked
    // open would be undetectable anyway. Matches the widget's own pill
    // (Empty.svelte, <a target="_blank">), which is never blocked.
    const a = document.createElement("a");
    a.href = `https://wa.me/${channelContext().whatsappPhone.replace(/[^\d]/g, "")}`;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.click();
    return true;
  },
  sms() {
    if (!chatApi.channels().sms) return false;
    const smsPhone = channelContext().smsPhone;
    if (!smsPhone) return false;
    window.location.href = `sms:${smsPhone}`;
    return true;
  }
};

if (typeof window !== "undefined" && !window.RidestoreChat) {
  window.RidestoreChat = chatApi;
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", autoMount, { once: true });
} else {
  autoMount();
}
